import { NutritionalInfo } from "@/clients/models/NutritionalInfo";
import { LightningBoltIcon } from "@/components/icons/lightning-bolt-icon";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";
import { MacroColumn } from "./macro-column";

const MACRO_COLORS = {
  protein: "bg-[#27BC8A]",
  carbs: "bg-[#EFB100]",
  fats: "bg-os-brand",
} as const;

interface MealDetailMacrosProps {
  nutritionalInfo: NutritionalInfo;
}

export const MealDetailMacros = memo(function MealDetailMacros({
  nutritionalInfo,
}: MealDetailMacrosProps) {
  const proteinG = nutritionalInfo.perPortion?.protein?.grams ?? 0;
  const carbsG = nutritionalInfo.perPortion?.carbohydrate?.total?.grams ?? 0;
  const fatsG = nutritionalInfo.perPortion?.fat?.total?.grams ?? 0;
  const calories = nutritionalInfo.perPortion?.energy?.kcal ?? 0;

  /**
   * Express each macro as a percentage of the total caloric contribution from
   * all three macros (protein 4 kcal/g, carbs 4 kcal/g, fats 9 kcal/g).
   * This avoids a hard-coded daily reference and gives a meaningful per-meal split.
   */
  const proteinKcal = proteinG * 4;
  const carbsKcal = carbsG * 4;
  const fatsKcal = fatsG * 9;
  const totalMacroKcal = proteinKcal + carbsKcal + fatsKcal;

  const toPct = (kcal: number) =>
    totalMacroKcal > 0 ? Math.round((kcal / totalMacroKcal) * 100) : 0;

  const proteinPct = toPct(proteinKcal);
  const carbsPct = toPct(carbsKcal);
  const fatsPct = toPct(fatsKcal);

  return (
    <div className="flex flex-col gap-6 gap-7.5 rounded-3xl border border-gray-100 pt-9 pb-11">
      <div className="flex flex-col items-center">
        <div className="flex w-full items-center gap-3">
          <div className="h-px flex-1 bg-gray-100" />
          <div className="flex w-50 items-center justify-center gap-2 text-[#512190]">
            <LightningBoltIcon className="h-5 w-5" />
            <span className={cn(textVariants.bodySingleLine, "font-semibold")}>
              {calories} kcal / serving
            </span>
          </div>
          <div className="h-px flex-1 bg-gray-100" />
        </div>

        <p className={cn(textVariants.footnote, "h-9 italic opacity-60")}>of the daily intake</p>
      </div>

      <div className="flex justify-center gap-11 px-8">
        <MacroColumn
          label="Protein"
          grams={proteinG}
          pct={proteinPct}
          barColor={MACRO_COLORS.protein}
        />
        <MacroColumn label="Carbs" grams={carbsG} pct={carbsPct} barColor={MACRO_COLORS.carbs} />
        <MacroColumn label="Fats" grams={fatsG} pct={fatsPct} barColor={MACRO_COLORS.fats} />
      </div>
    </div>
  );
});
